import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, UserPlus, KeyRound, ShieldOff, ShieldCheck, Trash2, X, Copy } from 'lucide-react';
import DashboardHeader from './DashboardHeader';
import DateFilter, { formatDisplayDate, allDateFilter } from './DateFilter';
import { usePagedQuery, dateRangeParams } from './usePagedQuery';
import { Pagination, SearchBox } from './TableControls';

const API = process.env.REACT_APP_BACKEND_API_URL;

const ROLE_LABEL = { admin: 'Admin', teamLead: 'Team Lead', recruiter: 'Recruiter' };
const STATUS_META = {
    active: { label: 'Active', bg: '#dcfce7', color: '#166534' },
    revoked: { label: 'Revoked', bg: '#fee2e2', color: '#b91c1c' }
};

const btn = {
    display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#fff', border: '1px solid #e2e8f0',
    padding: '6px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, color: '#334155', cursor: 'pointer'
};
const input = { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', width: '100%', boxSizing: 'border-box' };
const label = { fontSize: '12px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '4px' };

const formatDateTime = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '—';
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const Modal = ({ title, onClose, children }) => (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}>
        <div role="dialog" aria-label={title} style={{ background: '#fff', borderRadius: '10px', padding: '20px', width: '100%', maxWidth: '440px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <h3 style={{ margin: 0, fontSize: '16px', color: '#0f172a' }}>{title}</h3>
                <button onClick={onClose} aria-label="Close" style={{ ...btn, border: 'none', padding: '4px' }}><X size={16} /></button>
            </div>
            {children}
        </div>
    </div>
);

// Admin-only Account Control page. The backend independently enforces Admin-only
// access on every /api/admin/users* route (JWT + role), this page is just the UI.
const AccountControl = ({ user, onLogout }) => {
    const navigate = useNavigate();
    const [notice, setNotice] = useState('');
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [createdFilter, setCreatedFilter] = useState(allDateFilter);
    const [modal, setModal] = useState(null); // { type, account? }
    const [busy, setBusy] = useState(false);
    const [modalError, setModalError] = useState('');
    const [form, setForm] = useState({ name: '', email: '', password: '', role: 'recruiter' });
    const [newPassword, setNewPassword] = useState('');
    const [revealed, setRevealed] = useState(null); // { name, password } shown once

    // Server-side paging: search / role / status / created-date are applied by the API, newest account first.
    const params = useMemo(() => ({
        search: search.trim(),
        role: roleFilter === 'all' ? '' : roleFilter,
        status: statusFilter === 'all' ? '' : statusFilter,
        ...dateRangeParams(createdFilter, 'created')
    }), [search, roleFilter, statusFilter, createdFilter]);
    const table = usePagedQuery(`${API}/api/admin/users`, params);
    const users = table.rows;
    const loading = table.loading;
    const error = table.error || '';
    const load = table.refetch;

    const closeModal = () => { setModal(null); setModalError(''); setNewPassword(''); };

    const call = async (method, path, body) => {
        setBusy(true);
        setModalError('');
        try {
            const res = await fetch(`${API}${path}`, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: body ? JSON.stringify(body) : undefined
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) { setModalError(data.error || 'Request failed.'); return null; }
            return data;
        } catch (e) {
            setModalError('Server connection failed.');
            return null;
        } finally {
            setBusy(false);
        }
    };

    const flash = (msg) => { setNotice(msg); setTimeout(() => setNotice(''), 4000); };

    const submitCreate = async (e) => {
        e.preventDefault();
        const data = await call('POST', '/api/admin/users', form);
        if (data) { closeModal(); setForm({ name: '', email: '', password: '', role: 'recruiter' }); flash(`User "${data.name}" created.`); load(); }
    };
    const submitRole = async (acc, role) => {
        const data = await call('PATCH', `/api/admin/users/${acc.id}/role`, { role });
        if (data) { closeModal(); flash(`${acc.name} is now ${ROLE_LABEL[data.role] || data.role}.`); load(); }
    };
    const submitPassword = async (e) => {
        e.preventDefault();
        const acc = modal.account;
        const data = await call('PATCH', `/api/admin/users/${acc.id}/password`, newPassword ? { new_password: newPassword } : {});
        if (data) {
            closeModal();
            if (data.temporary_password) setRevealed({ name: acc.name, password: data.temporary_password });
            else flash(`Password updated for ${acc.name}.`);
            load();
        }
    };
    const submitAccess = async (acc, action) => {
        const data = await call('PATCH', `/api/admin/users/${acc.id}/access`, { action });
        if (data) { closeModal(); flash(`${acc.name}'s access ${action === 'revoke' ? 'revoked' : 'activated'}.`); load(); }
    };
    const submitDelete = async (acc) => {
        const data = await call('DELETE', `/api/admin/users/${acc.id}`);
        if (data) { closeModal(); flash(`${acc.name}'s account was deleted.`); load(); }
    };

    const visible = users;

    const th = { textAlign: 'left', padding: '10px', fontSize: '12px', color: '#475569', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' };
    const td = { padding: '10px', fontSize: '13px', color: '#0f172a', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' };

    return (
        <div style={{ fontFamily: 'Inter, sans-serif', background: '#f4f6f8', minHeight: '100vh', padding: '20px', boxSizing: 'border-box' }}>
            <DashboardHeader user={user} titleText="Account Control" onLogout={onLogout} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <button onClick={() => navigate('/admin')} style={btn}><ArrowLeft size={14} /> Back to Dashboard</button>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>Account Control</h2>
                <div style={{ flex: 1 }} />
                <button onClick={() => { setModal({ type: 'create' }); setModalError(''); }} style={{ ...btn, background: '#2563eb', color: '#fff', border: '1px solid #2563eb' }}>
                    <UserPlus size={14} /> Create User
                </button>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
                <SearchBox value={search} onChange={setSearch} placeholder="Search name or email" minWidth="240px" />
                <select style={{ ...input, width: 'auto' }} value={roleFilter} onChange={e => setRoleFilter(e.target.value)} aria-label="Filter by role">
                    <option value="all">All roles</option>
                    <option value="admin">Admin</option>
                    <option value="teamLead">Team Lead</option>
                    <option value="recruiter">Recruiter</option>
                </select>
                <select style={{ ...input, width: 'auto' }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)} aria-label="Filter by status">
                    <option value="all">All statuses</option>
                    <option value="active">Active</option>
                    <option value="revoked">Revoked</option>
                </select>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Created:</span>
                    <DateFilter dateField="created_at" initialValue={createdFilter} onFilterChange={setCreatedFilter} />
                </div>
            </div>

            {notice && <div style={{ background: '#dcfce7', color: '#166534', padding: '8px 12px', borderRadius: '6px', fontSize: '13px', marginBottom: '12px' }}>{notice}</div>}
            {error && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '8px 12px', borderRadius: '6px', fontSize: '13px', marginBottom: '12px' }}>{error}</div>}

            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
                    <thead>
                        <tr>
                            <th style={th}>Name</th><th style={th}>Email</th><th style={th}>Role</th><th style={th}>Status</th>
                            <th style={th}>Open Tasks</th><th style={th}>Created</th><th style={th}>Last Status Change</th><th style={th}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td style={td} colSpan={8}>Loading…</td></tr>}
                        {!loading && visible.length === 0 && <tr><td style={td} colSpan={8}>No users found.</td></tr>}
                        {!loading && visible.map(u => {
                            const meta = STATUS_META[u.status] || STATUS_META.active;
                            const isSelf = u.id === user?.id;
                            return (
                                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                                    <td style={{ ...td, fontWeight: 600 }}>{u.name}{isSelf ? ' (you)' : ''}</td>
                                    <td style={td}>{u.email}{!u.has_password && <span style={{ color: '#b45309', fontSize: '11px', marginLeft: '6px' }}>no password set</span>}</td>
                                    <td style={td}>
                                        <select
                                            value={u.role}
                                            disabled={isSelf || busy}
                                            onChange={e => setModal({ type: 'role', account: u, role: e.target.value })}
                                            style={{ ...input, width: 'auto', padding: '4px 6px' }}
                                            aria-label={`Role for ${u.name}`}
                                        >
                                            <option value="admin">Admin</option>
                                            <option value="teamLead">Team Lead</option>
                                            <option value="recruiter">Recruiter</option>
                                        </select>
                                    </td>
                                    <td style={td}><span style={{ background: meta.bg, color: meta.color, padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: 700 }}>{meta.label}</span></td>
                                    <td style={td}>{u.open_tasks ?? 0}</td>
                                    <td style={td}>{u.created_at ? formatDisplayDate(u.created_at) : '—'}</td>
                                    <td style={td}>{formatDateTime(u.status_changed_at)}{u.status_changed_by ? ` · ${u.status_changed_by}` : ''}</td>
                                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                        <button style={btn} onClick={() => { setModal({ type: 'password', account: u }); setModalError(''); setNewPassword(''); }}><KeyRound size={13} /> Reset Password</button>{' '}
                                        {u.status === 'revoked'
                                            ? <button style={btn} disabled={busy} onClick={() => setModal({ type: 'activate', account: u })}><ShieldCheck size={13} /> Activate</button>
                                            : <button style={btn} disabled={isSelf || busy} onClick={() => setModal({ type: 'revoke', account: u })}><ShieldOff size={13} /> Revoke</button>}{' '}
                                        <button style={{ ...btn, color: '#b91c1c' }} disabled={isSelf || busy} onClick={() => setModal({ type: 'delete', account: u })}><Trash2 size={13} /> Delete</button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <Pagination page={table.page} totalPages={table.totalPages} total={table.total} pageSize={table.pageSize}
                onPageChange={table.setPage} onPageSizeChange={table.setPageSize} loading={table.loading} label="users" />

            {modal?.type === 'create' && (
                <Modal title="Create User" onClose={closeModal}>
                    <form onSubmit={submitCreate} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div><label style={label}>Full name</label><input style={input} required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
                        <div><label style={label}>Email</label><input style={input} type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                        <div><label style={label}>Password (min 8 characters)</label><input style={input} type="text" required minLength={8} autoComplete="new-password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} /></div>
                        <div><label style={label}>Role</label>
                            <select style={input} value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                                <option value="recruiter">Recruiter</option><option value="teamLead">Team Lead</option><option value="admin">Admin</option>
                            </select>
                        </div>
                        {modalError && <div style={{ color: '#b91c1c', fontSize: '12px' }}>{modalError}</div>}
                        <button type="submit" disabled={busy} style={{ ...btn, background: '#2563eb', color: '#fff', justifyContent: 'center' }}>{busy ? 'Creating…' : 'Create User'}</button>
                    </form>
                </Modal>
            )}

            {modal?.type === 'password' && (
                <Modal title={`Reset Password — ${modal.account.name}`} onClose={closeModal}>
                    <form onSubmit={submitPassword} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div><label style={label}>New password (leave blank to generate one)</label>
                            <input style={input} type="text" minLength={newPassword ? 8 : undefined} autoComplete="new-password" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
                        </div>
                        {modalError && <div style={{ color: '#b91c1c', fontSize: '12px' }}>{modalError}</div>}
                        <button type="submit" disabled={busy} style={{ ...btn, background: '#2563eb', color: '#fff', justifyContent: 'center' }}>{busy ? 'Saving…' : 'Reset Password'}</button>
                    </form>
                </Modal>
            )}

            {['role', 'revoke', 'activate', 'delete'].includes(modal?.type) && (
                <Modal
                    title={{ role: 'Change Role', revoke: 'Revoke Access', activate: 'Activate Access', delete: 'Delete Account' }[modal.type]}
                    onClose={closeModal}
                >
                    <p style={{ fontSize: '13px', color: '#334155', marginTop: 0 }}>
                        {modal.type === 'role' && <>Change <b>{modal.account.name}</b> from {ROLE_LABEL[modal.account.role]} to <b>{ROLE_LABEL[modal.role]}</b>?</>}
                        {modal.type === 'revoke' && <><b>{modal.account.name}</b> will be signed out and unable to log in until access is activated again.</>}
                        {modal.type === 'activate' && <><b>{modal.account.name}</b> will be able to log in again.</>}
                        {modal.type === 'delete' && <>Permanently remove <b>{modal.account.name}</b>'s account and all access? Task and history records are kept. This cannot be undone.</>}
                    </p>
                    {modalError && <div style={{ color: '#b91c1c', fontSize: '12px', marginBottom: '8px' }}>{modalError}</div>}
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <button style={btn} onClick={closeModal}>Cancel</button>
                        <button
                            disabled={busy}
                            style={{ ...btn, background: modal.type === 'delete' || modal.type === 'revoke' ? '#b91c1c' : '#2563eb', color: '#fff' }}
                            onClick={() => {
                                if (modal.type === 'role') submitRole(modal.account, modal.role);
                                else if (modal.type === 'delete') submitDelete(modal.account);
                                else submitAccess(modal.account, modal.type);
                            }}
                        >
                            {busy ? 'Working…' : 'Confirm'}
                        </button>
                    </div>
                </Modal>
            )}

            {revealed && (
                <Modal title="Temporary Password" onClose={() => setRevealed(null)}>
                    <p style={{ fontSize: '13px', color: '#334155', marginTop: 0 }}>New password for <b>{revealed.name}</b>. It is shown only once — copy it now.</p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <input readOnly style={{ ...input, fontFamily: 'monospace' }} value={revealed.password} onFocus={e => e.target.select()} />
                        <button style={btn} onClick={() => { try { navigator.clipboard.writeText(revealed.password); } catch (e) { /* ignore */ } }}><Copy size={13} /> Copy</button>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default AccountControl;